// The Nazareth world map — one compact, interconnected place. Pure data (no
// engine import): dimensions, static structures, resource-node spawns, NPCs,
// prayer spots and codex-scroll locations. play.ts brings it to life.
import type { MaterialKey } from './data.js';

export const WORLD_W = 2600;
export const WORLD_H = 1920;

// ── Static structures ────────────────────────────────────────────────────────
export type StructKind =
  | 'home' | 'workshop' | 'well' | 'synagogue' | 'house' | 'stall' | 'gate' | 'oliveDecor' | 'bush'
  | 'garden' | 'trough' | 'store' | 'byre';

export interface Struct {
  kind: StructKind;
  x: number; y: number; w: number; h: number;
  tint?: string;
  solid?: boolean;         // blocks Joseph (footprint near the base)
  label?: string;
}

export const STRUCTS: Struct[] = [
  // Home & workshop compound (left-centre)
  { kind: 'home',     x: 300, y: 980,  w: 300, h: 240, solid: false, label: 'Home' },
  { kind: 'workshop', x: 640, y: 1050, w: 320, h: 250, solid: false, label: 'Workshop' },

  // Village core (centre-right)
  { kind: 'well',      x: 1460, y: 980,  w: 120, h: 120, solid: true, label: 'The Well' },
  { kind: 'synagogue', x: 1820, y: 720,  w: 340, h: 280, solid: true, label: 'Synagogue' },
  { kind: 'house', x: 1180, y: 760,  w: 230, h: 200, solid: true, tint: '#e4d5b6' },
  { kind: 'house', x: 1520, y: 700,  w: 230, h: 200, solid: true, tint: '#e9dcc0' },
  { kind: 'house', x: 1260, y: 1120, w: 230, h: 200, solid: true, tint: '#e0cfac' },
  { kind: 'house', x: 1700, y: 1140, w: 230, h: 200, solid: true, tint: '#ecdfc2' },
  { kind: 'house', x: 2020, y: 1040, w: 230, h: 200, solid: true, tint: '#e4d5b6' },

  // Market stalls
  { kind: 'stall', x: 1440, y: 1230, w: 150, h: 110, tint: '#c0533f', label: 'Market' },
  { kind: 'stall', x: 1640, y: 1250, w: 150, h: 110, tint: '#b98a3c', label: 'Weaver' },

  // Decorative olive trees dotting the fields (non-gatherable, just lovely)
  { kind: 'oliveDecor', x: 1360, y: 1560, w: 90, h: 120 },
  { kind: 'oliveDecor', x: 1560, y: 1660, w: 90, h: 120 },
  { kind: 'oliveDecor', x: 1820, y: 1560, w: 90, h: 120 },
  { kind: 'oliveDecor', x: 2040, y: 1680, w: 90, h: 120 },
  { kind: 'oliveDecor', x: 2220, y: 1540, w: 90, h: 120 },
  { kind: 'oliveDecor', x: 1240, y: 1700, w: 90, h: 120 },
  // The byre, where the working animals stand when they are not out with him.
  { kind: 'byre', x: 1000, y: 1178, w: 200, h: 132, label: 'The Byre' },

  // The timber store behind the open-air shop — the room Joseph walks into.
  { kind: 'store', x: 736, y: 934, w: 190, h: 118, label: 'Store' },

  // The village garden, worked by Naomi, and the trough at the pasture edge.
  { kind: 'garden', x: 1120, y: 1430, w: 280, h: 160, label: 'The Garden' },
  { kind: 'trough', x: 1560, y: 1452, w: 84, h: 26 },

  { kind: 'bush', x: 780, y: 900, w: 60, h: 46 },
  { kind: 'bush', x: 1120, y: 1020, w: 60, h: 46 },
  { kind: 'bush', x: 2000, y: 760, w: 60, h: 46 },
];

// Anchor points (world coords) for the interactive spots inside a structure.
export const SPOTS = {
  workbench: { x: 720, y: 1180 },     // the crafting station
  lumberPile: { x: 880, y: 1150 },
  maryHome: { x: 380, y: 1120 },
  jesusHome: { x: 470, y: 1150 },
  mealTable: { x: 430, y: 1080 },
  bed: { x: 330, y: 1070 },
  wellDraw: { x: 1520, y: 1110 },
  community: { x: 1520, y: 940 },     // the well canopy build site (above the well)
};

// ── Resource nodes ───────────────────────────────────────────────────────────
export type NodeKind =
  | 'branch' | 'tree' | 'log' | 'rock' | 'flax' | 'cloth'
  // heavy loads: they sit in the world whether or not an animal is with him,
  // but he cannot lift them alone
  | 'logpile' | 'boulder';

/** Which working animal is needed to take a heavy load. */
export type AnimalId = 'donkey' | 'ox';

export interface NodeDef {
  id: string;
  kind: NodeKind;
  x: number; y: number;
  gives: MaterialKey;   // primary material
  /** Heavy loads only: the animal that must be with him to take it. */
  needs?: AnimalId;
}

/** Where a working animal lives, and what it can carry. */
export interface WorkAnimalDef {
  id: AnimalId;
  name: string;
  /** Its standing place at the byre. */
  home: { x: number; y: number };
}

export const WORK_ANIMALS: WorkAnimalDef[] = [
  { id: 'donkey', name: 'the donkey', home: { x: 1054, y: 1300 } },
  { id: 'ox',     name: 'the ox',     home: { x: 1146, y: 1306 } },
];

// Grove (top), Hills/Quarry (bottom-left), Fields (bottom-right).
export const NODES: NodeDef[] = [
  // Grove — fallen branches (easy wood) & trees (chop mini-timing)
  { id: 'br1', kind: 'branch', x: 520, y: 360, gives: 'wood' },
  { id: 'br2', kind: 'branch', x: 700, y: 260, gives: 'wood' },
  { id: 'br3', kind: 'branch', x: 900, y: 420, gives: 'wood' },
  { id: 'br4', kind: 'branch', x: 1120, y: 300, gives: 'wood' },
  { id: 'br5', kind: 'branch', x: 1320, y: 440, gives: 'wood' },
  { id: 'br6', kind: 'branch', x: 640, y: 520, gives: 'wood' },
  { id: 'tr1', kind: 'tree', x: 460, y: 220, gives: 'wood' },
  { id: 'tr2', kind: 'tree', x: 820, y: 180, gives: 'wood' },
  { id: 'tr3', kind: 'tree', x: 1080, y: 200, gives: 'wood' },
  { id: 'tr4', kind: 'tree', x: 1260, y: 240, gives: 'wood' },
  { id: 'lg1', kind: 'log', x: 1000, y: 520, gives: 'timber' },
  { id: 'lg2', kind: 'log', x: 1420, y: 300, gives: 'timber' },
  { id: 'lg3', kind: 'log', x: 560, y: 150, gives: 'timber' },

  // Bushels of cut logs — three Wood at once, but only the donkey can bear one.
  { id: 'lp1', kind: 'logpile', x: 760, y: 430, gives: 'wood', needs: 'donkey' },
  { id: 'lp2', kind: 'logpile', x: 1180, y: 520, gives: 'wood', needs: 'donkey' },
  { id: 'lp3', kind: 'logpile', x: 420, y: 250, gives: 'wood', needs: 'donkey' },

  // Hills / Quarry — stone
  { id: 'rk1', kind: 'rock', x: 360, y: 1560, gives: 'stone' },
  { id: 'rk2', kind: 'rock', x: 540, y: 1680, gives: 'stone' },
  { id: 'rk3', kind: 'rock', x: 720, y: 1560, gives: 'stone' },
  { id: 'rk4', kind: 'rock', x: 460, y: 1760, gives: 'stone' },

  // Large stones — three Stone at once, and only the ox can shift one.
  { id: 'bo1', kind: 'boulder', x: 620, y: 1700, gives: 'stone', needs: 'ox' },
  { id: 'bo2', kind: 'boulder', x: 300, y: 1680, gives: 'stone', needs: 'ox' },
  { id: 'bo3', kind: 'boulder', x: 830, y: 1656, gives: 'stone', needs: 'ox' },
  { id: 'rk5', kind: 'rock', x: 820, y: 1720, gives: 'stone' },
  { id: 'rk6', kind: 'rock', x: 300, y: 1700, gives: 'stone' },

  // Fields — flax (rope) & the weaver's cloth
  { id: 'fx1', kind: 'flax', x: 1980, y: 1560, gives: 'rope' },
  { id: 'fx2', kind: 'flax', x: 2180, y: 1660, gives: 'rope' },
  { id: 'fx3', kind: 'flax', x: 1720, y: 1720, gives: 'rope' },
  { id: 'cl1', kind: 'cloth', x: 1715, y: 1300, gives: 'cloth' },
];

// ── Villagers ────────────────────────────────────────────────────────────────
export interface NpcDef {
  id: string;
  name: string;
  color: string;        // robe colour
  /** What they are doing when you find them — drives the prop at their feet. */
  role: 'water' | 'market' | 'weave' | 'field' | 'teach' | 'garden' | 'play';
  home: { x: number; y: number };
  /** Their own corner of Nazareth during the day — the well, a stall, the
   *  garden, the fields. Deliberately spread: the village felt empty because
   *  four of them stood on the same square yard by the well. */
  work: { x: number; y: number };
  /** How far they drift around their station while idling, in world units. */
  wander: number;
}

export const NPCS: NpcDef[] = [
  { id: 'hannah',     name: 'Hannah',    color: '#7a6ba0', role: 'water',  home: { x: 1250, y: 900 },  work: { x: 1598, y: 1152 }, wander: 16 },
  { id: 'eli',        name: 'Eli',       color: '#6f7f3f', role: 'field',  home: { x: 2090, y: 1180 }, work: { x: 1912, y: 1566 }, wander: 34 },
  { id: 'child_ruth', name: 'Ruth',      color: '#c98a3f', role: 'play',   home: { x: 1330, y: 1260 }, work: { x: 1330, y: 1424 }, wander: 46 },
  { id: 'susanna',    name: 'Susanna',   color: '#4f7f8f', role: 'weave',  home: { x: 1590, y: 840 },  work: { x: 1704, y: 1382 }, wander: 14 },
  { id: 'reuben',     name: 'Reuben',    color: '#a0553f', role: 'market', home: { x: 1770, y: 1280 }, work: { x: 1504, y: 1364 }, wander: 14 },
  { id: 'rabbi',      name: 'Rabbi Amos',color: '#5a5566', role: 'teach',  home: { x: 1990, y: 900 },  work: { x: 1994, y: 1032 }, wander: 12 },
  { id: 'naomi',      name: 'Naomi',     color: '#8a6f6a', role: 'garden', home: { x: 2100, y: 1010 }, work: { x: 1246, y: 1498 }, wander: 30 },
];

// ── Animals ──────────────────────────────────────────────────────────────────
// Pure ambience: nothing here is interactive, nothing asks for attention. Each
// animal drifts around its own anchor on a slow sine, so the world breathes
// without anything ever needing to be chased, fed or fought.
export type AnimalKind = 'goat' | 'sheep' | 'chicken' | 'donkey' | 'bird';

export interface AnimalDef {
  kind: AnimalKind;
  x: number; y: number;
  /** Drift radius around the anchor, in world units. */
  r: number;
}

export const ANIMALS: AnimalDef[] = [
  // Birds in the grove, where Joseph gathers his timber.
  { kind: 'bird', x: 560,  y: 300, r: 90 },
  { kind: 'bird', x: 880,  y: 240, r: 110 },
  { kind: 'bird', x: 1180, y: 330, r: 96 },
  { kind: 'bird', x: 700,  y: 470, r: 76 },
  { kind: 'bird', x: 1320, y: 210, r: 84 },
  { kind: 'bird', x: 420,  y: 420, r: 70 },

  // The village edge: a grazing pasture between the houses and the fields.
  { kind: 'goat',  x: 1508, y: 1524, r: 40 },
  { kind: 'goat',  x: 1592, y: 1582, r: 34 },
  { kind: 'goat',  x: 1440, y: 1600, r: 30 },
  { kind: 'sheep', x: 1672, y: 1540, r: 32 },
  { kind: 'sheep', x: 1744, y: 1596, r: 28 },
  { kind: 'sheep', x: 1616, y: 1648, r: 30 },
  { kind: 'sheep', x: 1780, y: 1494, r: 26 },

  // Chickens scratching about the houses.
  { kind: 'chicken', x: 1332, y: 1352, r: 22 },
  { kind: 'chicken', x: 1372, y: 1328, r: 20 },
  { kind: 'chicken', x: 1300, y: 1386, r: 18 },
  { kind: 'chicken', x: 1806, y: 1318, r: 20 },
  { kind: 'chicken', x: 1846, y: 1348, r: 18 },

  // A patient donkey by the market, and one out at the field edge.
  { kind: 'donkey', x: 1386, y: 1288, r: 10 },
  { kind: 'donkey', x: 1988, y: 1470, r: 14 },
];

// ── Prayer spots ─────────────────────────────────────────────────────────────
export interface PrayerSpotDef { id: string; x: number; y: number; label: string; }
export const PRAYER_SPOTS: PrayerSpotDef[] = [
  { id: 'home',      x: 420, y: 1160, label: 'Pray at home' },
  { id: 'synagogue', x: 1990, y: 1000, label: 'Pray at the synagogue' },
  { id: 'hill',      x: 980, y: 1740, label: 'Pray on the quiet hill' },
];

// ── Codex scrolls in the world ───────────────────────────────────────────────
export interface ScrollDef { id: string; entry: string; x: number; y: number; }
export const SCROLLS: ScrollDef[] = [
  { id: 's1', entry: 'righteous',     x: 380, y: 300 },
  { id: 's2', entry: 'dreams',        x: 1180, y: 470 },
  { id: 's3', entry: 'egypt',         x: 2260, y: 1500 },
  { id: 's4', entry: 'nazareth',      x: 250, y: 1520 },
  { id: 's5', entry: 'carpenter_son', x: 636, y: 1392 },
  { id: 's6', entry: 'tekton',        x: 900, y: 1240 },
  { id: 's7', entry: 'village',       x: 1620, y: 620 },
  { id: 's8', entry: 'household',     x: 610, y: 1300 },
  { id: 's9', entry: 'foster',        x: 380, y: 940 },
  { id: 's10', entry: 'patron_workers', x: 2120, y: 780 },
  { id: 's11', entry: 'patron_church',  x: 2000, y: 1300 },
  { id: 's12', entry: 'go_to_joseph',   x: 1520, y: 1420 },
];

/** Which broad region a world point sits in (for the location banner). */
export function regionAt(x: number, y: number): string {
  if (y < 640) return 'The Grove';
  if (y > 1420 && x < 1050) return 'The Hills';
  if (y > 1400 && x >= 1050) return 'The Fields';
  if (x < 1000 && y >= 940 && y <= 1360) return 'Home & Workshop';
  return 'Nazareth Village';
}
